import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Socket } from "socket.io-client";

import {
  checkSocketConnectionAsync,
  errors,
  getSocket,
  IClientToServerEvents,
  IRideStatusUpdate,
  IRydeEvent,
  IServerToClientEvents,
  Log,
} from "@ryde/shared";

import { useApplication } from "../application";
import { useRideDtoActions } from "./use-ride-dto-actions";
import { useRidesListActions } from "./use-rides-list-actions";

export const useRidesSocketActions = () => {
  const { t } = useTranslation();
  const { updateRidesStatus } = useRidesListActions();
  const { getEditingRideDtoId, updateEditingRideStatus } = useRideDtoActions();
  const { isMainApp } = useApplication();
  useEffect(() => {
    if (!isMainApp) {
      return;
    }

    let socket: Socket<IServerToClientEvents, IClientToServerEvents> | null =
      null;

    const onRideBulkUpdate = (data: Partial<IRydeEvent>): void => {
      if (data.eventName === "RIDE_BULK_UPDATE_STATUS") {
        const updateRidesData = data.data as IRideStatusUpdate[];
        updateRidesStatus(updateRidesData);

        const currentRideId = getEditingRideDtoId();
        const updateCurrentRideData = updateRidesData.find(
          x => x.id === currentRideId,
        );

        if (updateCurrentRideData) {
          updateEditingRideStatus(updateCurrentRideData.status);
        }
      }
    };

    const setupSocket = async (): Promise<void> => {
      try {
        const isConnected = await checkSocketConnectionAsync(undefined, {
          allowCookieAuth: true,
        });

        if (!isConnected) {
          Log.warn(t(errors.common.socketNotConnected));
          return;
        }

        socket = getSocket();

        if (!socket) {
          Log.warn(t(errors.common.socketNotConnected));
          return;
        }

        socket?.on("event", onRideBulkUpdate);
      } catch (error) {
        Log.warnAndToast(t(errors.common.socketNotConnected), error);
      }
    };

    setupSocket();

    return () => {
      socket?.off("event", onRideBulkUpdate);
    };
  }, [
    getEditingRideDtoId,
    isMainApp,
    t,
    updateEditingRideStatus,
    updateRidesStatus,
  ]);
};
